import React, { useMemo, useCallback, useEffect, useState } from 'react';
import compact from 'lodash/compact';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import concat from 'lodash/concat';
import uniqBy from 'lodash/uniqBy';
import size from 'lodash/size';

import { NanoID } from '../../../../types';

import {
  MultiSelectExtendedDataType,
  MultiSelectChangeCallbackType,
  MultiSelectInputChangeCallbackType
} from '../../../../helpers/MultiSelect/types';

import {
  FETCH_CACHE_SELECT_FIELD_TEAMS_QUERY,
  FetchCacheSelectFieldTeamsQueryResponse
} from '../../queries/fetchCacheSelectFieldTeams.query';

import { useCachePaginatedTeams } from '../../hooks/useCachePaginatedTeams';

import { MultiSelect } from '../../../../helpers/MultiSelect';

import { TeamCache } from '../../TeamCache';

const options = {
  staleTime: 1000 * 60 * 60
};

const selectedOptions = {
  staleTime: 1000 * 60 * 60,
  keepPreviousData: true
};

interface TeamsMultiSelectProps {
  value: NanoID[];
  autoFocus?: boolean;
  onChange: (value: NanoID[]) => void;
}

function TeamsMultiSelect({
  value,
  autoFocus,
  onChange
}: TeamsMultiSelectProps) {
  const [cachedSelectedData, setCachedSelectedData] = useState<
    MultiSelectExtendedDataType[]
  >([]);

  const { teams, teamsFetched, teamsError, changeTeamsFilters } =
    useCachePaginatedTeams<FetchCacheSelectFieldTeamsQueryResponse>({
      cacheKey: TeamCache.selectFieldCacheKey(),
      query: FETCH_CACHE_SELECT_FIELD_TEAMS_QUERY,
      options
    });

  const {
    teams: selectedTeams,
    teamsError: selectedTeamsError,
    teamsFilters: selectedTeamsFilters,
    teamsFetched: selectedTeamsFetched,
    changeTeamsFilters: changeSelectedTeamsFilters
  } = useCachePaginatedTeams<FetchCacheSelectFieldTeamsQueryResponse>({
    cacheKey: TeamCache.selectFieldSelectedTeamsCacheKey(),
    query: FETCH_CACHE_SELECT_FIELD_TEAMS_QUERY,
    initialFilters: {
      nanoId: { in: value }
    },
    initialLimit: 1000,
    options: selectedOptions
  });

  useEffect(() => {
    if (!isEqual(value, selectedTeamsFilters?.nanoId?.in)) {
      changeSelectedTeamsFilters({ nanoId: { in: value } });
    }
  }, [value, selectedTeamsFilters, changeSelectedTeamsFilters]);

  const data = useMemo<MultiSelectExtendedDataType[]>(() => {
    return teams.map(({ nanoId, name, image }) => ({
      value: nanoId,
      label: name,
      image: image?.file
    }));
  }, [teams]);

  const selectedData = useMemo<MultiSelectExtendedDataType[]>(() => {
    return selectedTeams.map(({ nanoId, name, image }) => ({
      value: nanoId,
      label: name,
      image: image?.file
    }));
  }, [selectedTeams]);

  const multiSelectValue = useMemo<MultiSelectExtendedDataType[]>(() => {
    const dataWithSelectedValues = selectedData
      ? uniqBy(concat(selectedData, data, cachedSelectedData), 'value')
      : data;

    return isArray(value)
      ? compact(
          value.map((item) => find(dataWithSelectedValues, ['value', item]))
        )
      : [];
  }, [value, data, selectedData, cachedSelectedData]);

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (value: MultiSelectExtendedDataType) => {
      if (isArray(value)) {
        setCachedSelectedData(value);
      }
      onChange(isArray(value) ? value.map((item) => item.value) : []);
    },
    [onChange, setCachedSelectedData]
  );

  const handleInputChange = useCallback<MultiSelectInputChangeCallbackType>(
    (input) => {
      changeTeamsFilters(
        { name: { ilike: input } },
        compact([input ? null : 'name'])
      );
    },
    [changeTeamsFilters]
  );

  return (
    <MultiSelect
      error={teamsError || selectedTeamsError}
      inputWrapperClassName="block"
      onChange={handleChange}
      value={multiSelectValue}
      multi={true}
      isSearchable={true}
      optionsLoading={!teamsFetched}
      isLoading={
        !selectedTeamsFetched && size(value) !== size(multiSelectValue)
      }
      data={data}
      classNamePrefix="av_multi_select"
      autoFocus={autoFocus}
      onInputChange={handleInputChange}
    />
  );
}

export default TeamsMultiSelect;
